import { canonicalizeDateTime } from "@repo/shared";
import * as React from "react";
import { DatePicker } from "@/components/ui/date-picker";
import { TimePicker } from "@/components/ui/time-picker";
import { useDialogGuard } from "@/hooks/use-dialog-guard";
import {
  formatLocalDateInput,
  formatLocalTimeInput,
  localDateTimeToUtcField,
} from "@/lib/date-utils";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./date-time-picker.i18n";

/** QL-211 — the preset list offers every quarter hour; any HH:mm can be typed. */
const MINUTE_STEP = 15;

type Parts = { date: string; time: string };

const EMPTY: Parts = { date: "", time: "" };

/** The stored UTC value as the viewer's local date and time. */
function toLocalParts(value: string | null | undefined): Parts {
  const canonical = value ? canonicalizeDateTime(value) : null;
  if (!canonical) return EMPTY;
  return { date: formatLocalDateInput(canonical), time: formatLocalTimeInput(canonical) };
}

export type DateTimePickerProps = {
  /** Stored form: UTC `YYYY-MM-DDTHH:mm:ssZ`, or "" / null when unset. */
  value?: string | null;
  /** Called with the stored form, or "" when the date is cleared. */
  onChange: (value: string) => void;
  /**
   * Called when focus leaves the whole picker (moving between its date and
   * time inputs does not count). Auto-saving callers flush their save here:
   * the native time input reports every segment edit as a change.
   */
  onBlur?: () => void;
  id?: string;
  name?: string;
  disabled?: boolean;
  className?: string;
  required?: boolean;
};

/**
 * QL-211 — a date and a time of day for a `datetime` field. Shows and takes
 * the viewer's local time; emits UTC `YYYY-MM-DDTHH:mm:ssZ`.
 *
 * Clearing the date clears the whole value. Either part may be entered first:
 * a date without a complete time, or a time without a date, is held here and
 * not emitted — the field keeps its previous value and a hint asks for the
 * missing part — so a datetime is never saved as an implicit midnight.
 */
export function DateTimePicker({
  value,
  onChange: onChangeProp,
  onBlur,
  id,
  name,
  disabled = false,
  className,
  required,
}: DateTimePickerProps) {
  const t = useT(T);
  const { markDirty } = useDialogGuard();

  const committed = React.useMemo(() => toLocalParts(value), [value]);
  // A half-entered value: one part entered, the other still missing.
  const [draft, setDraft] = React.useState<Parts | null>(null);
  // A new value from outside (a save, a reset) replaces any half-entered one.
  // Reset while rendering rather than in an effect, so the stale draft is
  // never painted for a frame.
  const [draftFor, setDraftFor] = React.useState(value);
  if (draftFor !== value) {
    setDraftFor(value);
    setDraft(null);
  }
  const shown = draft ?? committed;

  const commit = (next: Parts) => {
    const stored = localDateTimeToUtcField(next.date, next.time);
    if (stored === null) {
      setDraft(next);
      return;
    }
    setDraft(null);
    if (stored !== value) onChangeProp(stored);
  };

  const handleDateChange = (date: string) => {
    // Emptying the date box clears the whole value, time included.
    if (date === "") {
      setDraft(null);
      if (value) onChangeProp("");
      return;
    }
    commit({ ...shown, date });
  };

  const handleTimeChange = (time: string) => {
    // The preset list is a bare button; the dialog guard must still see the
    // change (DatePicker reports its own).
    markDirty();
    commit({ ...shown, time });
  };

  const complete = localDateTimeToUtcField(shown.date, shown.time) !== null;
  const missingTime = shown.date !== "" && !complete;
  const missingDate = shown.date === "" && shown.time !== "";
  const timeId = id ? `${id}-time` : undefined;
  const hintId = id ? `${id}-hint` : undefined;

  return (
    // The date and time inputs as one group; the blur handler watches focus
    // leaving the group as a whole. min-w-0: a fieldset will not shrink below
    // its content otherwise.
    <fieldset
      className={cn("min-w-0 space-y-1", className)}
      onBlur={(e) => {
        if (onBlur && !e.currentTarget.contains(e.relatedTarget as Node | null)) onBlur();
      }}
    >
      <div className="flex gap-2">
        <div className="min-w-0 flex-1">
          <DatePicker
            id={id}
            name={name}
            value={shown.date}
            onChange={handleDateChange}
            disabled={disabled}
            required={required || shown.time !== ""}
            aria-describedby={missingDate ? hintId : undefined}
          />
        </div>
        <div className="w-[120px] shrink-0">
          <TimePicker
            id={timeId}
            value={shown.time}
            onChange={handleTimeChange}
            disabled={disabled}
            required={required || shown.date !== ""}
            minuteStep={MINUTE_STEP}
            aria-label={t.time}
            aria-describedby={missingTime ? hintId : undefined}
          />
        </div>
      </div>
      {(missingTime || missingDate) && !disabled && (
        <p id={hintId} className="text-xs text-destructive">
          {missingTime ? t.enterTime : t.enterDate}
        </p>
      )}
    </fieldset>
  );
}
